var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-ai-rail> — THE AI rail. One element, every app.
 *
 * The rail is a product surface, not a per-app layout detail: it must look and behave identically in
 * the gateway app, the fleet admin, the AI console and everything after them. So the width, the
 * open/close animation, the drag-to-resize wall and the persistence all live HERE, and an app
 * contributes exactly one thing — what goes inside it:
 *
 *   <vitesse-ai-rail ?open=${this.aiOpen}>
 *     <vitesse-ai-panel .manager=${this.aiManager}></vitesse-ai-panel>
 *   </vitesse-ai-rail>
 *
 * SHADOW DOM, because the content arrives through a <slot>. A light-DOM version of this element
 * cannot work: <slot> is inert outside a shadow root, and the element's own render() would replace
 * the very children the app passed in. Slotted content keeps its OUTER styling — it stays in the
 * app's light DOM and is styled by the app's stylesheet — so the panel still gets the host's tokens
 * and SCSS. Shadow DOM here costs nothing and is the only thing that composes.
 *
 * NO TITLE ROW, deliberately: the rail is narrow and the header's AI button already names it, so a
 * heading spent a line of vertical space restating what the user just clicked.
 */
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { RailResizeController, resizeWallCss } from './rail-resize';
/** What the main content keeps, whatever the rail beside it is asking for. Below this the app is
 *  not a narrower app, it is a gutter. */
const MIN_CONTENT_W = 360;
import { holdSelection, releaseSelection, watchSelection } from '../ai/screen';
let VitesseAiRail = class VitesseAiRail extends LitElement {
    constructor() {
        super(...arguments);
        /** Whether the rail is open. The host owns this — the header button toggles it. */
        this.open = false;
        /**
         * THE WIDTH THE CONTENT IS ASKING FOR, when the content manages its own.
         *
         * The AI panel draws the reader's place beside the places it was reached through, as a stack of
         * columns that each own and remember their width. The rail cannot be the owner then — it would be
         * one knob for several independent ones — so the panel announces the total and the rail becomes
         * the container for it. Announced as an event the rail listens for ON ITSELF rather than bound by
         * the app, so no app has to know the panel can do this.
         *
         * `null` means "I do not manage a width": the rail keeps its own dragged width and its own wall,
         * exactly as it always has, which is the marketplace rail and the panel's hub and composer.
         *
         * The existing width transition is what makes a column slide in from the left and back out again.
         */
        this.managedWidth = null;
        /** A wall inside the stack is being dragged right now. The rail's own controller knows nothing
         *  about it, and the rail's width transition has to come off for it — see `applyWidth`. */
        this.columnsDragging = false;
        this.onColumns = (e) => {
            const d = e.detail;
            const w = d?.width ?? null;
            this.managedWidth = typeof w === 'number' && w > 0 ? w : null;
            this.columnsDragging = d?.dragging === true;
            /*
             * WRITTEN NOW, NOT NEXT FRAME.
             *
             * `managedWidth` is state, so on its own this width would land after Lit's next update — one
             * frame behind the columns, which set their own widths synchronously as the pointer moves. The
             * rail clips what overflows it, so for that frame the stack was wider than the rail and the
             * overflow was cut off the LEFT edge: the leftmost column's divider vanished mid-drag and came
             * back when the pointer stopped. The state assignment above is still what keeps a later
             * re-render correct; this is what keeps the frame right.
             */
            this.applyWidth();
        };
        this.rail = new RailResizeController(this, {
            maxWidth: () => this.maxWidth,
            storageKey: () => this.storageKey,
            // Its own wall, applied on the same terms as a column's: now, not next frame.
            onChange: () => this.applyWidth(),
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
    }
    /**
     * The rail's width on screen.
     *
     * A rail that drags its OWN wall is bounded by its own maximum — what one rail may ever take up —
     * and by the room the main content keeps behind it. Past either it stops being a rail beside an
     * app and becomes an app beside a gutter, which is not a width anyone wants to reach by dragging
     * slightly too far.
     *
     * A MANAGED width is a stack, and the rail's own maximum is the wrong bound for it: three columns
     * are three rails, and holding them to the width of one left every column pinned at its floor
     * with no wall able to move. The panel has already applied its own cap — per column, against the
     * same room — so the only thing left to enforce here is the promise about the app behind it.
     */
    get totalWidth() {
        if (this.managedWidth === null)
            return this.rail.width;
        const room = (globalThis.innerWidth || 0) - MIN_CONTENT_W;
        return room > 0 ? Math.min(this.managedWidth, room) : this.managedWidth;
    }
    /*
     * THE SELECTION SURVIVES THE CLICK THAT OPENS THIS.
     *
     * Selecting text and then reaching for the assistant is the whole "explain this" gesture, and
     * pressing the assistant button collapses the selection before any of our code runs. The rail is
     * where this belongs rather than in each app: it is the one component every surface opens, so
     * holding on open and releasing on close covers all of them and cannot be forgotten by the next
     * app to adopt the panel.
     */
    willUpdate(changed) {
        if (!changed.has('open'))
            return;
        if (this.open)
            holdSelection();
        else
            releaseSelection();
    }
    connectedCallback() {
        super.connectedCallback();
        // Remembering has to start BEFORE the click, so it starts when the rail mounts, not when it opens.
        watchSelection();
        this.addEventListener('vitesse-ai-columns', this.onColumns);
    }
    disconnectedCallback() {
        this.removeEventListener('vitesse-ai-columns', this.onColumns);
        super.disconnectedCallback();
    }
    static { this.styles = [
        resizeWallCss,
        css `
      :host {
        flex-shrink: 0;
        overflow: hidden;
        display: block;
        position: relative;
        width: 0;
        transition: width 0.22s ease;
      }
      :host([open]) {
        width: var(--ai-rail-w, 360px);
      }
      /*
     * A width transition driven by a pointermove handler makes the edge lag the cursor, which reads
     * as a broken drag rather than a smoothed one — and here it did worse than lag.
     *
     * The attribute used to track only the rail's OWN wall, so dragging a wall inside the stack left
     * this transition running: the columns moved with the pointer and the rail crept after them over
     * 220ms, clipping whatever had not been reached yet. It now covers any wall in the rail; see
     * the applyWidth method.
     */
      :host([resizing]) {
        transition: none;
      }

      /*
     * PINNED TO THE RIGHT EDGE, which is what makes the width animation mean something.
     *
     * The inner keeps its FULL width while the host animates, so the rail slides rather than
     * reflowing its contents on the way. WHICH edge it is pinned to decides which end that slide
     * happens at — and the rail's contents grow leftward, because the rail itself is against the
     * right edge of the screen. Anchored left (the default) the overflow is clipped on the right, so
     * a stack gaining a column appeared to eat its own outermost one while the rail widened.
     *
     * Anchored right, the clip is on the LEFT: a new column is revealed exactly where it belongs,
     * nothing already on screen moves, and closing plays it backwards.
     */
      .inner {
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        /*
       * THE SAME VALUE THE HOST READS, so the two can never be a frame apart.
       *
       * This was an inline width bound in the template while the host read a custom property set on
       * update, and a drag changed them in different frames: the inner jumped, the host followed,
       * and in between the rail was narrower than its own contents and clipped them. One property,
       * written once, and the close animation still works — the host's width goes to 0 while the
       * inner keeps this, which is what makes the panel slide out instead of reflowing on the way.
       */
        width: var(--ai-rail-w, 360px);
        display: flex;
        flex-direction: column;
        background: var(--bg-primary);
        /* Its own stacking context, so a page's sticky header inside the rail (z-index 50 in its
         own tree) can never rise above the wall's z-index 10 and cover the divider. */
        z-index: 0;
      }
    `,
    ]; }
    render() {
        return html `
      <!-- Only reachable while open; an 11px drag target on a 0px rail is a trap. -->
      <!-- Only while the rail owns its width: when the content is a stack, the leftmost column
           draws the wall at exactly this position and owns the drag. Two handles on one line is
           two controls for one gesture. -->
      ${this.open && this.managedWidth === null ? html `<div class="wall" @pointerdown=${this.rail.start} title=${t('ai.resize')}></div>` : null}
      <!-- The inner keeps its FULL width while the host animates to 0, so closing slides the panel
           out instead of reflowing its contents on the way. -->
      <div class="inner">
        <slot></slot>
      </div>
    `;
    }
    /**
     * The width, and whether it is currently being dragged, onto the element itself.
     *
     * Both the host and the inner read `--ai-rail-w`, so one write moves the whole rail in one frame.
     * Called from `updated` for every ordinary change, and synchronously from `onColumns` for the one
     * change that cannot wait for a render — a pointer moving a wall.
     */
    applyWidth() {
        this.style.setProperty('--ai-rail-w', `${this.totalWidth}px`);
        // ANY wall in the rail, not just the rail's own: a column's drag has to take the transition off
        // too, or the rail crawls after the columns and clips them on the way.
        this.toggleAttribute('resizing', this.rail.resizing || this.columnsDragging);
    }
    updated() {
        this.applyWidth();
    }
};
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseAiRail.prototype, "open", void 0);
__decorate([
    property({ type: Number, attribute: 'max-width' })
], VitesseAiRail.prototype, "maxWidth", void 0);
__decorate([
    property({ attribute: 'storage-key' })
], VitesseAiRail.prototype, "storageKey", void 0);
__decorate([
    state()
], VitesseAiRail.prototype, "managedWidth", void 0);
VitesseAiRail = __decorate([
    customElement('vitesse-ai-rail')
], VitesseAiRail);
export { VitesseAiRail };
//# sourceMappingURL=vitesse-ai-rail.js.map